import { isNetworkRequestInFlight } from '@apollo/client/core/networkStatus'
import * as React from 'react'
import Helmet from 'react-helmet'

import { usePreloadLazyComponent } from '@emico/lazy'

import { isGroupedProduct, useGetProduct } from './GetProduct.query'
import ProductDetailView from './ProductDetailView'
import ProductPageTopBar from './ProductPageTopBar'
import StickyCta from './StickyCta'
import { useUncachedStock } from './useUncachedStock'
import ProductCanonicals from '../../canonicals/ProductCanonicals'
import {
    LazyCartPage,
    LazyCategoryPage,
    LazySizeGuideModal,
} from '../../chunks'
import Header from '../../layout/Header'
import HeaderContainer from '../../layout/Header/HeaderContainer'
import PageWrapper from '../../layout/PageWrapper'
import NotFoundPage from '../../NotFoundPage'
import PageLoader from '../../presentation/PageLoader'
import ProductBlueConicMeta from '../../ProductBlueConicMeta'
import publishBlueConicEvent from '../../publishBlueConicEvent'
import { pushPercolateTag } from '../../registerPercolatePreparePage'
import ProductRichSnippet from '../../richSnippets/Product'
import useViewItem from '../../utils/ga4/useViewItem'
import { useRootCategoryId } from '../useRootCategory'

interface OwnProps {
    id: number
}

type Props = OwnProps

const ProductPage = ({ id }: Props) => {
    // Prefetch chunks that will likely be viewed next
    // This prevents the white flashes that appear between page navigations
    // because the browser has to load the chunk first
    usePreloadLazyComponent(LazyCategoryPage)
    usePreloadLazyComponent(LazyCartPage)
    usePreloadLazyComponent(LazySizeGuideModal)

    useUncachedStock(id)

    const [showCtaButton, setShowCtaByButton] = React.useState(true)
    const [showCtaFooter, setShowCtaByFooter] = React.useState(true)

    // By utilizing returnPartialData we can use cached data from
    // the category page to do a optimistic update.
    const { data: product, networkStatus } = useGetProduct(id, {
        returnPartialData: false,
        notifyOnNetworkStatusChange: true,
    })

    const rootCategoryId = useRootCategoryId()
    const pushViewItem = useViewItem()

    React.useEffect(() => {
        if (
            !product ||
            !product.id ||
            !product.sku ||
            !product.name ||
            !product.priceRange ||
            !product.brand ||
            !rootCategoryId
        ) {
            return
        }
        pushViewItem(product)
        publishBlueConicEvent('product_view', [product.sku])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        product,
        product?.id,
        product?.sku,
        product?.name,
        product?.priceRange,
        product?.brand,
    ])

    React.useEffect(() => {
        pushPercolateTag(`cat_p_${product?.id}`)
    }, [product?.id])

    if (!isNetworkRequestInFlight(networkStatus) && !product?.id) {
        return <NotFoundPage />
    }

    // When coming from the category page, some data will be available
    // so this loader will not be shown. But we still need it
    // in case a user comes from google or a direct link.
    if (!product || !product.id || !product.name) {
        return (
            <PageWrapper pageType="product">
                <PageLoader fullScreen reason="Loading product data..." />
            </PageWrapper>
        )
    }

    return (
        <PageWrapper
            pageType="product"
            hideStickyCtaFooter={(value) => setShowCtaByFooter(value)}
            header={
                <>
                    <HeaderContainer>
                        <Header />
                    </HeaderContainer>
                </>
            }
        >
            <ProductPageTopBar product={product} />
            <Helmet>
                <title>{product.metaTitle}</title>
                <meta name="description" content={product.metaDescription} />
                {product.metaRobots && (
                    <meta name="robots" content={product.metaRobots} />
                )}
                {process.env.REACT_APP_HIGHSTREET_STORE_NAME &&
                    !isGroupedProduct(product) && (
                        <meta
                            property="al:ios:url"
                            content={`${process.env.REACT_APP_HIGHSTREET_STORE_NAME}://products/${product.sku}`}
                        />
                    )}
            </Helmet>
            <ProductCanonicals id={product.id} />
            {!isGroupedProduct(product) && <ProductRichSnippet {...product} />}
            {!isGroupedProduct(product) && (
                <ProductBlueConicMeta {...product} />
            )}
            <ProductDetailView
                product={product}
                hideStickyCta={(value) => setShowCtaByButton(value)}
            />
            <StickyCta
                product={product}
                showSticky={!showCtaButton && !showCtaFooter}
            />
        </PageWrapper>
    )
}

export default ProductPage
